import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import {
  RECORD_STATUS_OPTIONS,
  type RecordStatusFilter,
} from '@/types/record-status'

interface RecordStatusPillProps {
  value: RecordStatusFilter
  onChange: (value: RecordStatusFilter) => void
}

/**
 * Single-select filter pill for record status (Active / All / Deleted).
 * Matches the visual style of FilterPill but uses radio-style selection.
 */
export function RecordStatusPill({ value, onChange }: RecordStatusPillProps) {
  const current = RECORD_STATUS_OPTIONS.find((o) => o.value === value)
  const isDefault = value === 'active'

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 gap-1.5 text-xs font-normal',
            !isDefault && 'border-primary/50 bg-primary/5'
          )}
        >
          <span className="text-muted-foreground">Records:</span>
          <span className="font-medium">{current?.label ?? 'Active'}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {RECORD_STATUS_OPTIONS.map((option) => {
                const isSelected = value === option.value
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => onChange(option.value)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-full border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
